import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  Star, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  Plane, 
  Car, 
  Utensils,
  Shield,
  ArrowLeft,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react'
import { usePackage } from '../hooks/usePackage'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { API_CONFIG } from '../config/api'
import Seo from '../components/Seo'

interface BookingFormData {
  first_name: string
  last_name: string
  email: string
  telephone: string
  message: string
  pax: string
  dates: string
  origin: string
}

export default function PackageDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { packageData, loading, error } = usePackage(slug || '')
  const { franchiseData, loading: franchiseLoading } = useFranchiseData()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [bookingForm, setBookingForm] = useState<BookingFormData>({
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    message: '',
    pax: '',
    dates: '',
    origin: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const handleBookingClick = () => {
    setShowBookingModal(true)
    setSubmitSuccess(false)
    setSubmitError('')
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setBookingForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      // Construct the URL with query parameters
      const params = new URLSearchParams({
        'lead[first_name]': bookingForm.first_name,
        'lead[last_name]': bookingForm.last_name,
        'lead[email]': bookingForm.email,
        'lead[telephone]': bookingForm.telephone,
        'lead[message]': bookingForm.message,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': bookingForm.pax,
        'lead[dates]': bookingForm.dates,
        'lead[origin]': bookingForm.origin,
        'lead[package_id]': packageData?.id?.toString() || ''
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setSubmitSuccess(true)
        setBookingForm({
          first_name: '',
          last_name: '',
          email: '',
          telephone: '',
          message: '',
          pax: '',
          dates: '',
          origin: ''
        })
        setTimeout(() => {
          setShowBookingModal(false)
          setSubmitSuccess(false)
        }, 2000)
      } else {
        const errorData = await response.text()
        console.error('API Error:', response.status, errorData)
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (error) {
      setSubmitError('Failed to submit your booking request. Please try again.')
      console.error('Booking submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getImageUrl = (pkg: any) => {
    if (pkg?.cover_photo?.large_url) {
      return pkg.cover_photo.large_url
    }
    if (pkg?.cover_photo?.url) {
      return pkg.cover_photo.url
    }
    if (pkg?.scraped_cover_photo) {
      return pkg.scraped_cover_photo
    }
    return null
  }

  const extractNightsFromName = (name: string): number | null => {
    if (!name) return null

    // Look for patterns like "2 nights", "3 night", etc.
    const nightsMatch = name.match(/(\d+)\s+nights?/i)
    if (nightsMatch) {
      return parseInt(nightsMatch[1], 10)
    }

    return null
  }

  const getNights = (pkg: any): number | null => {
    // Use the nights field if it exists and is not null
    if (pkg.nights !== null && pkg.nights !== undefined) {
      return pkg.nights
    }

    // Try to extract from name
    return extractNightsFromName(pkg.name)
  }

  const getDuration = (pkg: any): string => {
    const nights = getNights(pkg)
    if (nights) {
      return `${nights} ${nights === 1 ? 'Night' : 'Nights'}`
    }

    // If we have a duration field and it's not showing 'null nights' or similar issues
    if (pkg.duration && !pkg.duration.toLowerCase().includes('null')) {
      return pkg.duration
    }

    // Fallback
    return 'Duration TBA'
  }

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === 'null') return ''

    try {
      // Handle various date formats
      const date = new Date(dateString)

      // Check if date is valid (not 1970 epoch)
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return ''
      }

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return ''
    }
  }

  const getDates = (pkg: any): string => {
    // Debug logging
    console.log('Package date fields:', {
      dates: pkg.dates,
      travel_from: pkg.travel_from,
      travel_to: pkg.travel_to,
      valid_from: pkg.valid_from,
      valid_to: pkg.valid_to
    })

    // First try to use the dates field if it exists and is not null/empty
    if (pkg.dates && pkg.dates !== 'null' && pkg.dates !== null && pkg.dates.toString().trim() !== '') {
      return pkg.dates
    }

    // Then try travel_from and travel_to
    if (pkg.travel_from && pkg.travel_to &&
        pkg.travel_from !== 'null' && pkg.travel_to !== 'null' &&
        pkg.travel_from !== null && pkg.travel_to !== null) {
      const fromDate = formatDate(pkg.travel_from)
      const toDate = formatDate(pkg.travel_to)
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`
      }
    }

    // Fall back to validity dates
    if (pkg.valid_from && pkg.valid_to &&
        pkg.valid_from !== 'null' && pkg.valid_to !== 'null' &&
        pkg.valid_from !== null && pkg.valid_to !== null) {
      const fromDate = formatDate(pkg.valid_from)
      const toDate = formatDate(pkg.valid_to)
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`
      }
    }

    // Final fallback
    return 'Dates TBA'
  }

  const getAllImages = (pkg: any) => {
    const images = []
    
    // Add cover photo first
    const coverUrl = getImageUrl(pkg)
    if (coverUrl) {
      images.push({
        url: coverUrl,
        large_url: coverUrl,
        thumb_url: coverUrl
      })
    }
    
    // Add additional photos
    if (pkg?.photos && pkg.photos.length > 0) {
      images.push(...pkg.photos)
    }
    
    return images
  }

  const parseHtmlToList = (html: string) => {
    if (!html) return []
    const listItems = html.match(/<li>(.*?)<\/li>/g)
    if (!listItems) return []
    return listItems.map(item => item.replace(/<[^>]*>/g, '').trim())
  }

  const nextImage = () => {
    if (packageData) {
      const images = getAllImages(packageData)
      setCurrentImageIndex((prev) => (prev + 1) % images.length)
    }
  }

  const prevImage = () => {
    if (packageData) {
      const images = getAllImages(packageData)
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
    }
  }

  if (loading || franchiseLoading) {
    return (
      <div className="bg-white min-h-screen">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
          <div className="animate-pulse">
            {/* Header skeleton */}
            <div className="h-6 bg-gray-300 rounded w-32 mb-8"></div>
            <div className="h-12 bg-gray-300 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-gray-300 rounded w-1/2 mb-8"></div>
            
            {/* Image skeleton */}
            <div className="aspect-[16/9] bg-gray-300 rounded-2xl mb-8"></div>
            
            {/* Content skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-32 bg-gray-300 rounded"></div>
                <div className="h-24 bg-gray-300 rounded"></div>
                <div className="h-40 bg-gray-300 rounded"></div>
              </div>
              <div className="space-y-4">
                <div className="h-64 bg-gray-300 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !packageData) {
    return (
      <div className="bg-white min-h-screen">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Package Not Found</h1>
            <p className="text-gray-600 mb-8">
              {error || 'The package you are looking for does not exist.'}
            </p>
            <Link to="/packages" className="btn-primary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Packages
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const images = getAllImages(packageData)
  const currentImage = images[currentImageIndex]
  const inclusions = parseHtmlToList(packageData.inclusions)
  const exclusions = parseHtmlToList(packageData.exclusions)

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title={`${packageData.name} – ${packageData.location} | Memento Travel Co.`}
        description={packageData.intro || packageData.description?.replace(/<[^>]*>/g, '').slice(0, 160)}
        image={getImageUrl(packageData) || '/Logo%20Design%20for%20Memento%20Travel%20white.png'}
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined}
        type="service"
        pageType="package-detail"
        packageData={{
          id: slug || packageData.id?.toString() || '',
          name: packageData.name,
          description: packageData.intro || packageData.description?.replace(/<[^>]*>/g, '') || '',
          price: packageData.price_cents ? packageData.price_cents / 100 : undefined,
          currency: 'ZAR',
          destinations: packageData.location ? [packageData.location] : undefined,
          duration: packageData.nights ? `${packageData.nights} nights` : undefined,
          images: images.map(i => i.large_url || i.url).filter(Boolean),
          rating: typeof packageData.rating === 'number' ? packageData.rating : undefined,
          reviewCount: packageData.review_count || undefined
        }}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
        {/* Back button */}
        <Link 
          to="/packages" 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Packages
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium"
              style={{ 
                backgroundColor: `${franchiseData?.accent_color || '#F896C7'}20`,
                color: franchiseData?.button_color || '#F464AC'
              }}
            >
              {packageData.category}
            </span>
            <div className="flex items-center gap-1">
              {Array.from({ length: packageData.rating || 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4" style={{color: '#006D77', fill: '#006D77'}} />
              ))}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {packageData.name}
          </h1>
          
          <div className="flex items-center gap-6 text-gray-600">
            <div className="flex items-center gap-1">
              <MapPin className="h-5 w-5" />
              <span>{packageData.location}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-5 w-5" />
              <span>{getDuration(packageData)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-5 w-5" />
              <span>{packageData.total_pax}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-5 w-5" />
              <span>{getDates(packageData)}</span>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="relative mb-12">
          <div 
            className="aspect-[16/9] w-full rounded-2xl flex items-center justify-center"
            style={{ 
              backgroundColor: franchiseData?.accent_color || '#F896C7',
              backgroundImage: currentImage ? `url(${currentImage.large_url || currentImage.url})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {!currentImage && <Plane className="h-24 w-24 text-white opacity-60" />}
          </div>
          
          {/* Image navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              
              {/* Image indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* Quick info badges */}
          <div className="absolute top-6 left-6 flex gap-3">
            {packageData.flight_included && (
              <div className="text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1" style={{background: '#006D77'}}>
                <Plane className="h-3 w-3" />
                Flight Included
              </div>
            )}
            {packageData.hotel_included && (
              <div className="text-white px-3 py-1 rounded-full text-sm font-medium" style={{backgroundColor: '#F7F7E9', color: '#333'}}>
                Hotel Included
              </div>
            )}
            {packageData.transfer_included && (
              <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <Car className="h-3 w-3" />
                Transfers
              </div>
            )}
          </div>
        </div>

        {/* Thumbnail gallery */}
        {images.length > 1 && (
          <div className="mb-12 flex gap-4 overflow-x-auto pb-4">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                  index === currentImageIndex ? 'border-primary-600' : 'border-gray-200'
                }`}
              >
                <img
                  src={image.thumb_url || image.url}
                  alt={`Package image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Package Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Package</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-4">
                {packageData.intro}
              </p>
              <div 
                className="text-gray-600 leading-relaxed prose max-w-none"
                dangerouslySetInnerHTML={{ __html: packageData.description }}
              />
            </div>

            {/* Inclusions */}
            {inclusions.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  What's Included
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {inclusions.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exclusions */}
            {exclusions.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <X className="h-5 w-5 text-red-600" />
                  What's Not Included
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {exclusions.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <X className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Terms and Conditions */}
            {packageData.terms && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Terms & Conditions
                </h3>
                <div 
                  className="text-gray-600 leading-relaxed prose max-w-none bg-gray-50 p-4 rounded-lg"
                  dangerouslySetInnerHTML={{ __html: packageData.terms }}
                />
              </div>
            )}

            {/* Package Features */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Package Features</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Plane className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-sm font-medium text-gray-900">
                    {packageData.flight_included ? 'Flights' : 'No Flights'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {packageData.flight_included ? 'Included' : 'Not included'}
                  </div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Utensils className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <div className="text-sm font-medium text-gray-900">
                    {packageData.hotel_included ? 'Accommodation' : 'No Hotel'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {packageData.hotel_included ? 'Included' : 'Not included'}
                  </div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Car className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <div className="text-sm font-medium text-gray-900">
                    {packageData.transfer_included ? 'Transfers' : 'No Transfers'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {packageData.transfer_included ? 'Included' : 'Not included'}
                  </div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Shield className="h-8 w-8 mx-auto mb-2" style={{color: '#006D77'}} />
                  <div className="text-sm font-medium text-gray-900">
                    {packageData.guided_tour ? 'Guided Tour' : 'Self Guided'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {packageData.guided_tour ? 'Tour guide included' : 'Explore independently'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold mb-2" style={{ color: franchiseData?.button_color || '#F464AC' }}>
                    From {packageData.formatted_price}
                  </div>
                  <div className="text-sm text-gray-500">per person sharing</div>
                  <div className="text-sm text-gray-500">{packageData.total_pax}</div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-medium">{getDuration(packageData)}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Travel Dates</span>
                    <span className="font-medium text-sm">{getDates(packageData)}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Destination</span>
                    <span className="font-medium">{packageData.location}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Package Type</span>
                    <span className="font-medium">{packageData.category}</span>
                  </div>
                </div>

                <button className="w-full btn-primary mb-4" onClick={handleBookingClick}>
                  Book This Package
                </button>
                
                <Link to="/contact" className="w-full btn-secondary block text-center">
                  Contact Us for Details
                </Link>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Need Help?</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Our travel experts are here to help you plan the perfect trip.
                  </p>
                  <div className="text-sm">
                    <div className="text-gray-600">Call us:</div>
                    <div className="font-medium" style={{ color: franchiseData?.button_color || '#F464AC' }}>
                      {franchiseData?.telephone}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

             {/* Booking Modal */}
       {showBookingModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
           <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
             <div className="flex justify-between items-center mb-6">
               <h2 className="text-2xl font-bold text-gray-900">Book This Package</h2>
               <button
                 onClick={() => setShowBookingModal(false)}
                 className="text-gray-400 hover:text-gray-600 transition-colors"
               >
                 <X className="h-6 w-6" />
               </button>
             </div>

             {submitSuccess ? (
               <div className="text-center py-8">
                 <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                   <Check className="h-8 w-8 text-green-600" />
                 </div>
                 <p className="text-green-600 text-lg font-medium">
                   Your booking request has been sent successfully!
                 </p>
                 <p className="text-gray-600 mt-2">
                   We'll get back to you shortly.
                 </p>
               </div>
             ) : (
               <form onSubmit={handleBookingSubmit} className="space-y-4">
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       First Name *
                     </label>
                     <input
                       type="text"
                       name="first_name"
                       value={bookingForm.first_name}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                       required
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Last Name *
                     </label>
                     <input
                       type="text"
                       name="last_name"
                       value={bookingForm.last_name}
                       onChange={handleInputChange}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                       required
                     />
                   </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Email Address *
                   </label>
                   <input
                     type="email"
                     name="email"
                     value={bookingForm.email}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     required
                   />
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Phone Number *
                   </label>
                   <input
                     type="tel"
                     name="telephone"
                     value={bookingForm.telephone}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     required
                   />
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Number of People *
                     </label>
                     <input
                       type="text"
                       name="pax"
                       value={bookingForm.pax}
                       onChange={handleInputChange}
                       placeholder="e.g., 2 adults"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                       required
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Travel Dates *
                     </label>
                     <input
                       type="text"
                       name="dates"
                       value={bookingForm.dates}
                       onChange={handleInputChange}
                       placeholder="e.g., Dec 2024"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                       required
                     />
                   </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Departure City *
                   </label>
                   <input
                     type="text"
                     name="origin"
                     value={bookingForm.origin}
                     onChange={handleInputChange}
                     placeholder="e.g., Johannesburg"
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     required
                   />
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Additional Message
                   </label>
                   <textarea
                     name="message"
                     value={bookingForm.message}
                     onChange={handleInputChange}
                     rows={3}
                     placeholder="Any special requests or questions..."
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                   />
                 </div>

                 {submitError && (
                   <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                     <p className="text-red-600 text-sm">{submitError}</p>
                   </div>
                 )}

                 <div className="flex gap-3 pt-4">
                   <button
                     type="button"
                     onClick={() => setShowBookingModal(false)}
                     className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                   >
                     Cancel
                   </button>
                   <button
                     type="submit"
                     disabled={isSubmitting}
                     className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                     style={{ 
                       backgroundColor: franchiseData?.button_color || '#F464AC',
                       borderColor: franchiseData?.button_color || '#F464AC'
                     }}
                   >
                     {isSubmitting ? 'Submitting...' : 'Submit Booking'}
                   </button>
                 </div>
               </form>
             )}
           </div>
         </div>
       )}
    </div>
  )
} 